import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import React, { useRef, useMemo, useCallback, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import throttle from 'lodash/throttle';
import { BiData } from '@wix/da-bi/pkg/events';
import { Url } from '@wix/da-url';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import NoteIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import IconButtonWithNotification from '@wix/da-ds/pkg/Buttons/IconButtonWithNotification';
import { usePushModal } from '../../../utils/hooks/usePushModal';
import { ModalType } from '../../../Modals/redux/types';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useVerification } from '../../../VerificationPopup/withVerification';
import LoadableDesktopNotesPanel from './DesktopNotesPanel/LoadableDesktopNotesPanel';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import s from './NotesButtonAndDropdown.scss';
const NotesButton = (_param)=>{
    var { count = 0, action } = _param, rest = _object_without_properties(_param, [
        "count",
        "action"
    ]);
    const { t } = useTranslation();
    const { isVerificationNeeded, pushVerificationPopup } = useVerification();
    /**
   * We need to be sure that users with screen readers know that this button will instead open the verification popup.
   *
   * Avoids confusion when they can't find their notes.
   */ const actionAndLabel = isVerificationNeeded ? {
        onClick: ()=>pushVerificationPopup('notes'),
        'aria-label': t('siteHeader.a11y.openAccountVerificationDialog')
    } : _object_spread_props(_object_spread({}, action), {
        'aria-label': t(count > 0 ? 'siteHeader.a11y.notes.withItems' : 'siteHeader.a11y.notes.withoutItems', {
            formattedCount: count
        })
    });
    return /*#__PURE__*/ React.createElement(IconButtonWithNotification, _object_spread({
        icon: NoteIcon,
        size: "medium",
        notificationColor: "red",
        count: count,
        biData: BiData({
            evid: 700,
            link_name: 'notes',
            indicator: count
        })
    }, actionAndLabel, rest));
};
function useMergedCounts(hasNewMsg) {
    var _sessionCounts, _sessionCounts1;
    const { counts: sessionCounts } = useContext(SessionContext);
    var _sessionCounts_notes, _sessionCounts_chats;
    let mergedCount = ((_sessionCounts_notes = (_sessionCounts = sessionCounts) === null || _sessionCounts === void 0 ? void 0 : _sessionCounts.notes) !== null && _sessionCounts_notes !== void 0 ? _sessionCounts_notes : 0) + ((_sessionCounts_chats = (_sessionCounts1 = sessionCounts) === null || _sessionCounts1 === void 0 ? void 0 : _sessionCounts1.chats) !== null && _sessionCounts_chats !== void 0 ? _sessionCounts_chats : 0);
    if (hasNewMsg) {
        mergedCount = mergedCount + 1;
    }
    return mergedCount;
}
const dropdownId = 'site-header-notes';
const Desktop = ({ className, iconClassName, numberClassName, hasNewMsg, disableDropdown })=>{
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();
    const isMounted = useIsMounted();
    const { isVerificationNeeded } = useVerification();
    const rootRef = useRef(null);
    const enterTimerRef = useRef();
    const mergedCount = useMergedCounts(hasNewMsg);
    const handleOpen = useCallback(()=>{
        setIsOpen(true);
    }, []);
    const handleClose = useCallback(()=>{
        setIsOpen(false);
    }, []);
    const throttledMouseLeave = useMemo(()=>throttle(()=>{
            clearTimeout(enterTimerRef.current);
            handleClose();
        }, 300), [
        handleClose
    ]);
    const onMouseEnter = ()=>{
        enterTimerRef.current = setTimeout(()=>{
            handleOpen();
        }, 100);
    };
    const onClosed = useCallback(()=>{
        throttledMouseLeave();
    }, [
        throttledMouseLeave
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: c(s['desktop-root'], className),
        onMouseEnter: onMouseEnter,
        onMouseLeave: onClosed,
        ref: rootRef
    }, !isVerificationNeeded && /*#__PURE__*/ React.createElement(TextButton, {
        className: s['a11y-toggle'],
        size: "medium",
        variant: "main",
        "aria-haspopup": true,
        "aria-expanded": isOpen,
        "aria-controls": dropdownId,
        onClick: isOpen ? handleClose : handleOpen
    }, t('siteHeader.notes.open')), /*#__PURE__*/ React.createElement(NotesButton, {
        iconButtonClassName: iconClassName,
        numberClassName: numberClassName,
        count: mergedCount,
        action: {
            href: Url.notesHomeLink()
        }
    }), /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "NotesButtonAndDropdown"
    }, isMounted && isOpen && !disableDropdown && /*#__PURE__*/ React.createElement(LoadableDesktopNotesPanel, {
        id: dropdownId,
        className: c(s['dropdown']),
        onClose: onClosed
    })));
};
const Mobile = ({ className, iconClassName, numberClassName, hasNewMsg, disableDropdown })=>{
    const pushModal = usePushModal(ModalType.MOBILE_NOTES_DRAWER);
    const mergedCount = useMergedCounts(hasNewMsg);
    return /*#__PURE__*/ React.createElement("div", {
        className: className
    }, /*#__PURE__*/ React.createElement(NotesButton, {
        count: mergedCount,
        iconButtonClassName: iconClassName,
        numberClassName: numberClassName,
        action: disableDropdown ? {
            href: Url.notesHomeLink()
        } : {
            onClick: pushModal
        }
    }));
};
export const NotesButtonAndDropdown = (props)=>{
    const isMobile = useContext(MobileContext);
    return isMobile ? /*#__PURE__*/ React.createElement(Mobile, props) : /*#__PURE__*/ React.createElement(Desktop, props);
};
NotesButtonAndDropdown.displayName = 'NotesButtonAndDropdown';
export default NotesButtonAndDropdown;
